import classNames from 'classnames';
import { ReactNode, useEffect, useRef, useState } from 'react';

type Props = {
  children: ReactNode;
  className?: string;
};

/**
 * Dark-themed horizontal scroll container. Replaces the native WebKit/Firefox
 * scrollbar with a thin dark one and fades out any content that extends past
 * the right edge when there is more to scroll to.
 */
export function DarkHorizontalScroll({ children, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const update = () => {
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);

    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, []);

  return (
    <div className={classNames('relative', className)}>
      <div
        ref={ref}
        className={classNames(
          'overflow-x-auto',
          '[scrollbar-width:thin] [scrollbar-color:#322F2C_transparent]',
          '[&::-webkit-scrollbar]:h-1.5',
          '[&::-webkit-scrollbar-track]:bg-transparent',
          '[&::-webkit-scrollbar-thumb]:bg-[#322F2C]',
          '[&::-webkit-scrollbar-thumb]:rounded-full',
          '[&::-webkit-scrollbar-thumb:hover]:bg-[#4a4642]',
        )}
      >
        {children}
      </div>
      <div
        aria-hidden
        className={classNames(
          'pointer-events-none absolute top-0 right-0 h-full w-10',
          'bg-gradient-to-l from-surface-elevated to-transparent',
          'transition-opacity duration-150',
          canScrollRight ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}
